import React from "react";
import { useTranslation } from "react-i18next";
import { AppraiserProperty } from "../../types/appraiser.types";
import { ContentCard } from "../../components/ContentCard";
import { Typography } from "common/components/Typography";
import { usePenCertificate } from "./hooks/usePenCertificate";

const PenRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <>
    <Typography variant="caption" textStyle={{ fontSize: 16 }}>
      {label}
    </Typography>
    <Typography variant="body">{value}</Typography>
  </>
);

interface PropertyPenCertificateProps {
  property: AppraiserProperty;
}

export const PropertyPenCertificate: React.FC<PropertyPenCertificateProps> = ({
  property,
}) => {
  const { t } = useTranslation();
  const { data: penCertificate, hasPenCertificate } = usePenCertificate(
    property.uniqueNr
  );

  if (!hasPenCertificate || !penCertificate) {
    return null;
  }

  return (
    <ContentCard
      title={t("penCertificate.title")}
      caption={t("penCertificate.description")}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          columnGap: "32px",
          rowGap: "12px",
          alignItems: "baseline",
        }}
      >
        {!!penCertificate.energy_class && (
          <PenRow
            label={t("penCertificate.energyClass")}
            value={penCertificate.energy_class}
          />
        )}
        {!!penCertificate.valid_until && (
          <PenRow
            label={t("penCertificate.validUntil")}
            value={penCertificate.valid_until.replaceAll("-", ".")}
          />
        )}
        {!!penCertificate.heating_energy_consumption_kwh_m2_year && (
          <PenRow
            label={t("penCertificate.heatingConsumption")}
            value={`${
              penCertificate.heating_energy_consumption_kwh_m2_year
            } ${t("property.units.heatingConsumption")}`}
          />
        )}
        {!!penCertificate.heated_area_m2 && (
          <PenRow
            label={t("penCertificate.heatedArea")}
            value={`${penCertificate.heated_area_m2} m²`}
          />
        )}
        {!!penCertificate.heat_source && (
          <PenRow
            label={t("penCertificate.heatSource")}
            value={penCertificate.heat_source}
          />
        )}
      </div>
    </ContentCard>
  );
};
